/**
 * Gravity Zone Trigger Behavior
 * 
 * Changes gravity for entities inside the trigger zone.
 * Restores original gravity when entities exit.
 */

import { Vector3 } from '@babylonjs/core';
import { EntityType } from '../../types/EntityTypes';
import type { TriggerBehavior } from '../../types/TriggerTypes';
import { logger } from '../../core/Logger';

export const gravityZoneBehavior: TriggerBehavior = {
  id: 'trigger:gravity_zone',
  displayName: 'Gravity Zone',
  type: EntityType.TRIGGER_ZONE,
  category: 'trigger',
  tags: ['physics', 'gravity'],
  
  parameters: {
    gravityX: {
      type: 'number',
      default: 0,
      min: -50,
      max: 50,
      description: 'Gravity X component'
    },
    gravityY: {
      type: 'number',
      default: -5.0,
      min: -50,
      max: 50,
      description: 'Gravity Y component (negative = down)'
    },
    gravityZ: {
      type: 'number',
      default: 0,
      min: -50,
      max: 50,
      description: 'Gravity Z component'
    },
    affectsPlayer: {
      type: 'boolean',
      default: true,
      description: 'Apply gravity change to player'
    },
    affectsObjects: {
      type: 'boolean',
      default: true,
      description: 'Apply gravity change to dynamic objects'
    }
  },
  
  onEnter: (api) => {
    const { ctx, services } = api;
    const { params } = ctx;
    
    const gravityX = (params.gravityX as number) ?? 0;
    const gravityY = (params.gravityY as number) ?? -5.0;
    const gravityZ = (params.gravityZ as number) ?? 0;
    const affectsPlayer = (params.affectsPlayer as boolean) ?? true;
    const affectsObjects = (params.affectsObjects as boolean) ?? true;
    
    // Check if we should affect this entity
    const shouldAffect = 
      (ctx.entrant.isPlayer && affectsPlayer) ||
      (ctx.entrant.type === EntityType.DYNAMIC_OBJECT && affectsObjects);
    
    if (!shouldAffect) return;
    
    const gravity = new Vector3(gravityX, gravityY, gravityZ);
    
    // Store which entities we've affected
    // Initialize metadata if needed
    if (!ctx.self.metadata) {
      (ctx.self as { metadata: Record<string, unknown> }).metadata = {};
    }
    const metadata = ctx.self.metadata!;
    if (!metadata.affectedEntities) {
      metadata.affectedEntities = new Set<string>();
    }
    (metadata.affectedEntities as Set<string>).add(ctx.entrant.id);
    
    // Apply gravity override
    services.physicsZones.setEntityGravity(ctx.entrant.id, gravity);
    
    logger.triggers.log(`🌐 Gravity zone: Applied (${gravityX}, ${gravityY}, ${gravityZ}) to ${ctx.entrant.id}`);
  },
  
  onExit: (api) => {
    const { ctx, services } = api;
    
    const affectedEntities = ctx.self.metadata?.affectedEntities as Set<string> | undefined;
    if (!affectedEntities?.has(ctx.exiter.id)) return;
    
    // Reset gravity
    services.physicsZones.resetEntityGravity(ctx.exiter.id);
    affectedEntities.delete(ctx.exiter.id);
    
    logger.triggers.log(`🌐 Gravity zone: Reset gravity for ${ctx.exiter.id}`);
  }
};
